import React, { useState, useEffect, useRef, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";
import PageContainer from "../../components/PageContainer/PageContainer";
import DispatchTrayCard from "../../components/DispatchTrayCard/DispatchTrayCard";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import PackageDetail from "../../components/PackageDetail/PackageDetail";
import LocalizationContext from "../../utils/LocalizationContext";
import request from "../../utils/request";
import WashingImg from "../../assets/images/WASHING.svg";
import SterilizationImg from "../../assets/images/ICONO STERILIZATION NEW.svg";
import { LuPackageCheck } from "react-icons/lu";
import { FaBan } from "react-icons/fa";
import t from "../../utils/translation";
import styles from "./ReleaseWashing.module.css";
import DispatchSearch from "../../components/DispatchSearch/DispatchSearch";
import { getScreenPermissions } from "../../utils/userPermissions";
import { Tooltip } from "@material-ui/core";
import CircularIndicator from "../../components/CircularProgressIndicators/CircularIndicator";
import { IoIosArrowForward } from "react-icons/io";
import Redirect from "../../utils/Redirect";

const color = (id) =>
  id === 3
    ? styles.orange
    : id === 2
    ? styles.green
    : id === 1
    ? styles.blue
    : id === 4
    ? styles.red
    : "";

const ReleaseCycle = () => {
  const locale = useContext(LocalizationContext);
  const [cycles, setCycles] = useState([]);
  const [cyclePackages, setCyclePackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCycle, setLoadingCycle] = useState(false);
  const [selectedSterilizer, setSelectedSterilizer] = useState(null);
  const [sterilizers, setSterilizers] = useState([]);
  const [cycleQuery, setCycleQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [searchLocked, setSearchLocked] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [hasPositive, setHasPositive] = useState(null);
  const [clickedPackages, setClickedPackages] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);
  const [generalObservations, setGeneralObservations] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [chargeStatus, setChargeStatus] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const getCycles = async (washerId) => {
    setLoadingCycle(true);
    try {
      let res = await request().get(
        `/api/chargewashing/cycle?washerId=${washerId}`
      );
      setCycles(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoadingCycle(false);
    }
  };
  const getWashers = async () => {
    setLoading(true);

    try {
      const response = await request().get(`/api/parameter/chargeWashing`);
      const s = response.data.washers.map((item) => ({
        id: item.washerId,
        name: item.name,
      }));
      setSterilizers(s);
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWashers();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsFocused(false);
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      activeSuggestionIndex >= 0 &&
      activeSuggestionIndex < suggestions.length
    ) {
      const el = document.getElementById(`suggestion-${activeSuggestionIndex}`);
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeSuggestionIndex, suggestions]);

  const handleCycleQueryChange = (e) => {
    const value = e.target.value;
    setCycleQuery(value);
    setSearched(false);
    setActiveSuggestionIndex(-1);
    if (value.trim()) {
      const filtered = cycles
        .filter((c) => String(c.cycleNumber).includes(value))
        .slice(0, 50);
      setSuggestions(filtered);
    } else {
      setSuggestions(cycles.slice(0, 50));
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (!searchLocked && selectedSterilizer) {
      if (!cycleQuery.trim()) {
        setSuggestions(cycles.slice(0, 50));
      } else {
        const filtered = cycles
          .filter((c) => String(c.cycleNumber).includes(cycleQuery))
          .slice(0, 50);
        setSuggestions(filtered);
      }
      setActiveSuggestionIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveSuggestionIndex((prev) =>
          prev + 1 >= suggestions.length ? 0 : prev + 1
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveSuggestionIndex((prev) =>
          prev - 1 < 0 ? suggestions.length - 1 : prev - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeSuggestionIndex !== -1) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        } else {
          handleSearchClick();
        }
      } else if (e.key === "Escape") {
        setSuggestions([]);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick();
    }
  };

  const handleSearchClick = () => {
    if (!searchLocked && cycleQuery.trim() !== "") {
      const found = cycles.find(
        (c) => String(c.cycleNumber) === cycleQuery.trim()
      );
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
      setSearched(true);
      setSearchLocked(true);
      setSearchLoading(true);
      if (found) {
        setSelectedCycle(found);
        fetchCyclePackages(found.chargeId)
          .then((result) => {
            setCyclePackages(result.packages);
            setHasPositive(result.hasPositive);
          })
          .finally(() => setSearchLoading(false));
      } else {
        setCyclePackages([]);
        setHasPositive(null);
        setSearchLoading(false);
      }
    } else {
      handleCancel();
    }
  };

  const handleSuggestionClick = (cycle) => {
    setCycleQuery(String(cycle.cycleNumber));
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    setSearched(true);
    setSearchLocked(true);
    setSearchLoading(true);
    setSelectedCycle(cycle);
    fetchCyclePackages(cycle.chargeId)
      .then((result) => {
        setCyclePackages(result.packages);
        setHasPositive(result.hasPositive);
      })
      .finally(() => setSearchLoading(false));
  };

  const handleCancel = () => {
    setSearchLocked(false);
    setCycleQuery("");
    setCyclePackages([]);
    setSearched(false);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    setClickedPackages([]);
    setSelectedPackageIds([]);
    setHasPositive(null);
    setSelectedCycle(null);
    setGeneralObservations("");
  };

  const fetchCyclePackages = async (chargeId) => {
    try {
      let res = await request().get(
        `/api/chargeWashing/packages?chargeId=${chargeId}`
      );
      return { packages: res.data, hasPositive: false };
    } catch (error) {
      toast.error("Error fetching packages");
      return { packages: [], hasPositive: false };
    }
  };

  const handlePackageClick = (pkg, moveTo) => {
    const pkgId = pkg.packageId;
    const alreadySelected = selectedPackageIds.includes(pkgId);
    const existing = clickedPackages.find((p) => p.packageId === pkgId);
    if (alreadySelected && existing?.moveTo === moveTo) {
      setSelectedPackageIds((prev) => prev.filter((id) => id !== pkgId));
      setClickedPackages((prev) => prev.filter((p) => p.packageId !== pkgId));
    } else {
      if (!alreadySelected) {
        setSelectedPackageIds((prev) => [...prev, pkgId]);
      }
      setClickedPackages((prev) => {
        const filtered = prev.filter((p) => p.packageId !== pkgId);
        return [...filtered, { ...pkg, moveTo }];
      });
    }
  };

  const handleSetAllMoveTo = (moveToValue) => {
    const updatedCyclePackages = cyclePackages.map((pkg) => ({
      ...pkg,
      moveTo: moveToValue,
    }));
    setCyclePackages(updatedCyclePackages);
    setClickedPackages((prev) => {
      const merged = new Map(
        [...prev, ...updatedCyclePackages].map((pkg) => [
          pkg.packageId,
          { ...pkg, moveTo: moveToValue },
        ])
      );
      const mergedArray = Array.from(merged.values());
      setSelectedPackageIds(mergedArray.map((pkg) => pkg.packageId));
      return mergedArray;
    });
  };

  const allPackagesSameMoveTo = (moveToValue) => {
    if (!cyclePackages.length && !clickedPackages.length) return false;
    return (
      cyclePackages.every((p) => p.moveTo === moveToValue) &&
      clickedPackages.every((p) => p.moveTo === moveToValue)
    );
  };

  const releasePackages = async () => {
    setLoading(true);
    try {
      await request().put("/api/chargeWashing/release-washing", {
        generalObservations,
        chargeStatus: chargeStatus ?? selectedCycle?.chargeStatus,
        packagesWashing: clickedPackages.map(pkg => ({
            packageNumber: pkg.packageNumber,
            moveTo: pkg.moveTo,
            observations: pkg.observations
          })),
        chargeWashingId: selectedCycle?.chargeId,
      });
      handleCancel();
      getCycles(selectedSterilizer);
      toast.success(t("BlankEntry129"));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error releasing");
    } finally {
      setLoading(false);
    }
  };

  const handlePackageObservation = (id, observations) => {
    setClickedPackages((prev) =>
      prev.map((pkg) => (pkg.packageId === id ? { ...pkg, observations } : pkg))
    );
  };

  const renderCycleDisplay = () => {
    if (searchLoading) {
      return <div className={styles.cycleDisplay}>{`${t("Loading")}`}</div>;
    }
    if (
      searched &&
      cycleQuery.trim() &&
      !cycles.some((c) => String(c.cycleNumber) === cycleQuery.trim())
    ) {
      return (
        <div className={`${styles.cycleDisplay} ${styles.negative}`}>
          {`${t("CycleNumberSpan")} ${cycleQuery} ${t("BlankEntry116")}`}
        </div>
      );
    }
    if (searched && cycleQuery.trim() && selectedCycle) {
      const statusClass = selectedCycle.cycleResult
        ? styles.positive
        : selectedCycle.totalIndicators !== selectedCycle.vinculatedIndicators
        ? styles.standBy
        : styles.negative;
      return (
        <div className={`${styles.cycleDisplay} ${statusClass}`}>
          <span className={styles.cycleNumber}>
            {t("CycleNumberSpan")}: {cycleQuery}
          </span>
          <CircularIndicator
            totalIndicators={selectedCycle.totalIndicators}
            vinculatedIndicators={selectedCycle.vinculatedIndicators}
            hasPositive={selectedCycle.cycleResult}
          />
        </div>
      );
    }
    return null;
  };

  const status = (value) => {
    return value === 9
      ? t("BlankEntry126", locale) || "Released"
      : value === 10
      ? t("BlankEntry127", locale) || "Rejected"
      : t("FinishedSpan", locale) || "Finished";
  };

  const handleCycleStatus = (id) => {
    if (id === chargeStatus) {
      setChargeStatus(null);
      setGeneralObservations("");
    } else {
      setChargeStatus(id);
      if (id === 9) {
        handleSetAllMoveTo(3);
      } else {
        handleSetAllMoveTo(1);
      }
    }
  };

  return (
    <PageContainer
      categoryId={2}
      currentStep={3}
      category={t("WashingSpan")}
      backUrl={"/appwashingmenu"}
      title={"Categoria liberar bandejas"}
      subtitle={selectedPackage?.packageId ? t("DetailSpan") : null}
      clickBack={selectedPackage?.packageId ? true : null}
      onClickBack={() => setSelectedPackage({})}
      progress={true}
    >
      {loading ? (
        <div className="spinnerContainer">
          <SpinnerQ />
        </div>
      ) : (
        <>
          {!selectedPackage?.packageId ? (
            <>
              <div className={styles.actionButtons}>
                <button
                  className={styles.storageButton}
                  onClick={() =>
                    Redirect({
                      redirect: true,
                      path: "/appmanagepackages",
                      parameters: {release: true}
                    })
                  }
                >
                  Sterilization <IoIosArrowForward />
                </button>
              </div>
              <div className={styles.containerLayout}>
                <div className={styles.leftColumn}>
                  <h1 className={styles.columnHeader}>Cycle</h1>
                  <h2 className={styles.leftColumnTitle}>
                    {"Seleccione una lavadora"}
                  </h2>

                  <select
                    className={styles.sterilizerSelect}
                    value={selectedSterilizer || ""}
                    onChange={(e) => {
                      if (
                        !getScreenPermissions(
                          "SterilizedPackages",
                          "Release",
                          "NOALERT"
                        )
                      ) {
                        return;
                      } else {
                        getCycles(e.target.value);
                        setSelectedSterilizer(e.target.value);
                        handleCancel();
                      }
                    }}
                  >
                    <option value="" disabled></option>
                    {sterilizers.map((ster) => (
                      <option key={ster.id} value={ster.id}>
                        {ster.name}
                      </option>
                    ))}
                  </select>
                  <h2 className={styles.leftColumnTitle}>
                    {"Seleccione una carga"}
                  </h2>
                  <DispatchSearch
                    cycleQuery={cycleQuery}
                    handleCycleQueryChange={handleCycleQueryChange}
                    handleFocus={handleFocus}
                    handleKeyDown={handleKeyDown}
                    inputRef={inputRef}
                    searchLocked={searchLocked}
                    loading={loadingCycle}
                    selectedSterilizer={selectedSterilizer}
                    handleSearchClick={handleSearchClick}
                    isFocused={isFocused}
                    suggestions={suggestions}
                    suggestionsRef={suggestionsRef}
                    activeSuggestionIndex={activeSuggestionIndex}
                    handleSuggestionClick={handleSuggestionClick}
                  />
                  <div className={styles.cycleSection}>
                    {renderCycleDisplay()}
                  </div>
                  {searched &&
                    cycleQuery.trim() &&
                    cycles.some(
                      (c) => String(c.cycleNumber) === cycleQuery.trim()
                    ) && (
                      <>
                        {selectedCycle?.chargeStatus === 4 && (
                          <div className={styles.buttonContainer}>
                            <Tooltip title={t("BlankEntry144")} arrow>
                              <div
                                className={`${styles.button} ${color(4)} ${
                                  chargeStatus === 10 ? styles.active : ""
                                }`}
                                onClick={() => handleCycleStatus(10)}
                              >
                                <FaBan className={styles.icon} />
                              </div>
                            </Tooltip>
                            <Tooltip title={t("BlankEntry141")} arrow>
                              <div
                                className={`${styles.button} ${color(2)} ${
                                  chargeStatus === 9 ? styles.active : ""
                                }`}
                                onClick={() => handleCycleStatus(9)}
                              >
                                <LuPackageCheck className={styles.icon} />
                              </div>
                            </Tooltip>
                          </div>
                        )}
                        {selectedCycle?.chargeStatus === 9 && (
                          <div className={styles.statusContainer}>
                            <Tooltip title={t("BlankEntry126")} arrow>
                              <div
                                className={`${styles.buttonStatus} ${color(
                                  2
                                )} ${styles.active}`}
                              >
                                <LuPackageCheck className={styles.icon} />
                              </div>
                            </Tooltip>
                            <p>{status(selectedCycle?.chargeStatus)}</p>
                          </div>
                        )}
                        {selectedCycle?.chargeStatus === 10 && (
                          <div className={styles.statusContainer}>
                            <Tooltip title={t("BlankEntry127")} arrow>
                              <div
                                className={`${styles.buttonStatus} ${color(
                                  4
                                )} ${styles.active}`}
                              >
                                <FaBan className={styles.icon} />
                              </div>
                            </Tooltip>
                            <p>{status(selectedCycle?.chargeStatus)}</p>
                          </div>
                        )}
                      </>
                    )}
                  {selectedCycle?.chargeStatus === 4 && chargeStatus && (
                    <div className={styles.generalObservationsContainer}>
                      <label className={styles.generalObservationsLabel}>
                        {t("BlankEntry114")}
                      </label>
                      <textarea
                        className={styles.generalObservationsInput}
                        value={generalObservations}
                        onChange={(e) => setGeneralObservations(e.target.value)}
                        placeholder={`${t("ObservationSpan")}...`}
                        disabled={!searched}
                      />
                    </div>
                  )}
                  <button
                    className={styles.confirmButton}
                    onClick={releasePackages}
                    disabled={!selectedCycle}
                  >
                    {loading ? `${t("BlankEntry110")}...` : t("ConfirmButton")}
                  </button>
                  <button
                    className={styles.cancelButton}
                    onClick={handleCancel}
                    disabled={!selectedCycle || loading}
                  >
                    {t("Cancel")}
                  </button>
                </div>
                <div className={styles.mainContent}>
                  <h1 className={styles.columnHeader}>Trays</h1>
                  <div className={styles.packageGrid}>
                    <AnimatePresence>
                      {cyclePackages.map((pkg) => (
                        <motion.div
                          key={pkg.packageId}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.3 }}
                        >
                          <DispatchTrayCard
                            pkg={pkg}
                            selected={selectedPackageIds.includes(
                              pkg.packageId
                            )}
                            moveTo={
                              clickedPackages.find(
                                (p) => p.packageId === pkg.packageId
                              )?.moveTo
                            }
                            releaseClick={handlePackageClick}
                            onDetailClick={setSelectedPackage}
                            observations={selectedPackageIds.includes(
                              pkg.packageId
                            )}
                            handleObservations={handlePackageObservation}
                            release={true}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.detailContainer}>
              <PackageDetail
                packageId={selectedPackage?.packageId}
                locale={locale}
                release={true}
              />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default ReleaseCycle;
